/**
 * <kk-product-list> — arrows + end-state for the carousel layout of snippets/kk-product-list.liquid.
 *
 * Scrolls the track by one card per click, and sets data-at-start / data-at-end / data-fits so the CSS can
 * fade or hide the arrows. Re-measures when the track resizes, scrolls or its cards are swapped
 * (KK Related Products replaces them after fetching). The grid layout needs none of this.
 */
class KKProductList extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.track = this.querySelector('[data-kk-pl-track]');
    if (!this.track) return;

    this.querySelector('[data-kk-pl-prev]')?.addEventListener('click', () => this.#scrollByCard(-1), { signal });
    this.querySelector('[data-kk-pl-next]')?.addEventListener('click', () => this.#scrollByCard(1), { signal });
    this.track.addEventListener('scroll', () => this.#update(), { passive: true, signal });

    this.resizeObserver = new ResizeObserver(() => this.#update());
    this.resizeObserver.observe(this.track);
    this.mutationObserver = new MutationObserver(() => this.#update());
    this.mutationObserver.observe(this.track, { childList: true });
    this.#update();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  /** @param {number} direction -1 or 1 */
  #scrollByCard(direction) {
    const card = this.track.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: reduced ? 'auto' : 'smooth' });
  }

  #update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    this.toggleAttribute('data-fits', max <= 2);
    this.toggleAttribute('data-at-start', scrollLeft <= 2);
    this.toggleAttribute('data-at-end', scrollLeft >= max - 2);
  }
}

if (!customElements.get('kk-product-list')) {
  customElements.define('kk-product-list', KKProductList);
}
